import React from "react";
import { Typography, Card, Descriptions, Alert, Tag, Space, Button } from "antd";
import { ExclamationCircleOutlined, CalendarOutlined, FileTextOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;

/**
 * 合同到期预警处理器组件
 * V5.0 更新：标准化处理器接口
 * 用于展示合同到期预警的详细信息
 */
const ContractExpiryWarningViewer = ({ actionItem, onConfirm, onReject, loading = false, readOnly = false }) => {
    const { additional_info, suggested_payload } = actionItem;
    const contractInfo = additional_info?.contract_info;

    // 处理确认
    const handleConfirm = () => {
        onConfirm({
            action_taken: "USER_CONFIRMED",
            user_remarks: "用户确认处理合同到期预警",
        });
    };

    // 处理拒绝
    const handleReject = () => {
        onReject({
            action_taken: "USER_REJECTED",
            user_remarks: "用户选择暂不处理合同到期预警",
        });
    };

    if (!contractInfo) {
        return <Alert message="数据错误" description="无法获取合同信息" type="error" showIcon />;
    }

    const { contract_name, contract_number, expiry_date, days_until_expiry, contract_type, supplier_name, contract_amount, responsible_person } = contractInfo;

    // 根据剩余天数确定警告级别
    const getWarningLevel = (days) => {
        if (days <= 0) {
            return { type: "error", color: "red", text: "已到期" };
        } else if (days <= 7) {
            return { type: "error", color: "red", text: "紧急" };
        } else if (days <= 30) {
            return { type: "warning", color: "orange", text: "警告" };
        } else {
            return { type: "info", color: "blue", text: "提醒" };
        }
    };

    const warningLevel = getWarningLevel(days_until_expiry);
    const expiryDateFormatted = dayjs(expiry_date).format("YYYY年MM月DD日");

    return (
        <div>
            {/* 警告信息 */}
            <Alert
                message={
                    <Space>
                        <ExclamationCircleOutlined />
                        <Text strong>合同到期预警</Text>
                        <Tag color={warningLevel.color}>{warningLevel.text}</Tag>
                    </Space>
                }
                description={days_until_expiry <= 0 ? `合同"${contract_name}"已于${expiryDateFormatted}到期，请及时处理！` : `合同"${contract_name}"将在${days_until_expiry}天后（${expiryDateFormatted}）到期，请提前做好续签或替换准备。`}
                type={warningLevel.type}
                showIcon
                style={{ marginBottom: 16 }}
            />

            {/* 合同详细信息 */}
            <Card
                title={
                    <Space>
                        <FileTextOutlined />
                        <Text strong>合同详细信息</Text>
                    </Space>
                }
                style={{ marginBottom: 16 }}
            >
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="合同名称" span={2}>
                        <Text strong>{contract_name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="合同编号">{contract_number}</Descriptions.Item>
                    <Descriptions.Item label="合同类型">{contract_type || "未指定"}</Descriptions.Item>
                    <Descriptions.Item label="供应商">{supplier_name || "未指定"}</Descriptions.Item>
                    <Descriptions.Item label="负责人">{responsible_person || "未指定"}</Descriptions.Item>
                    <Descriptions.Item label="合同金额" span={2}>
                        {contract_amount ? `¥${contract_amount.toLocaleString()}` : "未指定"}
                    </Descriptions.Item>
                    <Descriptions.Item label="到期日期" span={2}>
                        <Space>
                            <CalendarOutlined />
                            <Text type={days_until_expiry <= 7 ? "danger" : "default"}>{expiryDateFormatted}</Text>
                            <Tag color={warningLevel.color}>{days_until_expiry <= 0 ? "已到期" : `还有${days_until_expiry}天`}</Tag>
                        </Space>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* 建议操作 */}
            {suggested_payload?.system_operations && suggested_payload.system_operations.length > 0 && (
                <Card
                    title={
                        <Space>
                            <ClockCircleOutlined />
                            <Text strong>建议操作</Text>
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Paragraph>系统建议执行以下操作来处理此合同到期预警：</Paragraph>
                    {suggested_payload.system_operations.map((operation, index) => (
                        <Alert key={index} message={operation.operation_type === "CREATE_RECORD" ? "创建提醒记录" : operation.operation_type} description={operation.operation_parameters?.description || "执行相关操作"} type="info" showIcon style={{ marginBottom: 8 }} />
                    ))}
                </Card>
            )}

            {/* 操作按钮 */}
            <Card>
                <Space>
                    <Button type="primary" onClick={handleConfirm} loading={loading} icon={<ExclamationCircleOutlined />}>
                        确认处理
                    </Button>
                    <Button onClick={handleReject} loading={loading}>
                        暂不处理
                    </Button>
                </Space>
                <Paragraph style={{ marginTop: 8, marginBottom: 0, fontSize: "12px", color: "#666" }}>点击"确认处理"将执行系统建议的操作，点击"暂不处理"将忽略此预警。</Paragraph>
            </Card>
        </div>
    );
};

export default ContractExpiryWarningViewer;
